import { useSyncExternalStore } from 'react';

import {
  FILL_ICONS,
  STROKE_ICONS,
  TAB_ICONS,
  type FillIconName,
  type IconName,
  type StrokeIconName,
  type TabIconName,
} from './icons';

export type { IconName, TabIconName };

const isStroked = (name: IconName): name is StrokeIconName => name in STROKE_ICONS;

export interface IconProps {
  readonly name: IconName;
  /** 26 tab bar · 20 row leading · 19 chip · 17 chevron · 16 pill · 32 empty state. */
  readonly size?: number;
  readonly className?: string;
  /**
   * Only for an icon that is the sole content of a control. An icon beside a
   * label is decorative and must stay hidden, or a screen reader announces the
   * name twice.
   */
  readonly title?: string;
}

/* --- optical weight ---------------------------------------------------------- */

/** The size the drawing is authored at: a row's leading glyph. */
const REF_SIZE = 20;
/** The weight the set is drawn for, in grid units of the 24-unit viewBox. */
const REF_WEIGHT = 1.9;
/**
 * How fast the weight is allowed to fall as the glyph grows.
 *
 * A stroke that scales 1:1 with the glyph is optically *too light* when small
 * and too heavy when large — the eye reads absolute thickness, not the ratio.
 * A type designer answers that with a sub-linear ramp: rendered thickness grows
 * as size^(2/3), so the weight in grid units falls as the cube root of the size
 * ratio. One third is the exponent, and it is the whole idea in one number.
 */
const OPTICAL_EXPONENT = 1 / 3;
/** Guard rails: outside 14–32px the curve would leave what the paths are drawn for. */
const MIN_WEIGHT = 1.6;
const MAX_WEIGHT = 2.2;

/**
 * Stroke weight in grid units, continuous in size.
 *
 * It used to be a three-step function of size buckets, which meant an 18px and a
 * 19px glyph standing next to each other came out at visibly different weights
 * for no reason a reader could see. Before that the renderer set
 * `vector-effect: non-scaling-stroke`, pinning the stroke to 1.5 *CSS* pixels and
 * swinging its relative weight by 2× between a 16px pill and a 32px empty state —
 * which is why the set read as machine-made. Neither is here now: one smooth
 * curve, no `vector-effect`, no `shape-rendering`.
 *
 * Pure and monotonic, so it can be asserted directly: 20 → 1.9, and every larger
 * size is lighter than every smaller one.
 */
export const strokeWidth = (size: number): number => {
  // A missing or nonsense size falls back to the reference rather than NaN,
  // which would silently drop the attribute and draw a hairline.
  const px = Number.isFinite(size) && size > 0 ? size : REF_SIZE;
  const raw = REF_WEIGHT * (REF_SIZE / px) ** OPTICAL_EXPONENT;
  const clamped = Math.min(MAX_WEIGHT, Math.max(MIN_WEIGHT, raw));
  // Hundredths of a grid unit is ~0.013px at 32 — below a retina subpixel, and it
  // keeps the emitted attribute readable instead of 1.7411589...
  return Math.round(clamped * 100) / 100;
};

export function Icon({ name, size = 20, className, title }: IconProps) {
  const stroked = isStroked(name);
  const paths = stroked ? STROKE_ICONS[name] : FILL_ICONS[name as FillIconName];

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={stroked ? 'none' : 'currentColor'}
      fillRule="evenodd"
      {...(stroked
        ? {
            stroke: 'currentColor',
            strokeWidth: strokeWidth(size),
            strokeLinecap: 'round' as const,
            strokeLinejoin: 'round' as const,
          }
        : {})}
      className={className}
      {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
      focusable="false"
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/* --- the tab glyph ----------------------------------------------------------- */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const motionQuery = (): MediaQueryList | null =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? null
    : window.matchMedia(REDUCED_MOTION);

const subscribeToMotion = (onChange: () => void) => {
  const query = motionQuery();
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
};

/**
 * Honour the setting in JS rather than leaving it to a stylesheet.
 *
 * The transition below lives in inline styles, so a `@media` block cannot reach
 * it — and "the animation is off unless someone else remembers to turn it off"
 * is not an accessibility guarantee. This is the guarantee; any CSS agent 5 adds
 * on `.tabbar__glyph` is belt and braces.
 */
const useReducedMotion = (): boolean =>
  useSyncExternalStore(
    subscribeToMotion,
    () => motionQuery()?.matches ?? false,
    () => false,
  );

/** Fade and settle are both well inside the 250ms an interface can spend on a tap. */
const FADE_MS = 180;
const SETTLE_MS = 220;
/** Where the incoming solid starts. Small enough to be felt rather than watched. */
const SETTLE_FROM = 0.86;
/** The app's ease, with a literal fallback so the shorthand can never parse to nothing. */
const EASE_ENTER = 'var(--ease-out, cubic-bezier(0.22, 1, 0.36, 1))';
/** Slow to leave: see below. */
const EASE_EXIT = 'cubic-bezier(0.5, 0, 1, 1)';

/**
 * The two layers do not share an easing curve, and that is the whole trick.
 *
 * Two glyphs crossfading on the same curve are both near 0.5 halfway through, and
 * where they overlap the composite alpha falls to about 0.75 — the mark visibly
 * goes pale in the middle of the swap. Letting the outgoing layer hold and then
 * drop while the incoming one rises immediately keeps the sum at ~1 throughout,
 * so what the eye sees is the hole filling in, not a dissolve.
 */
const glyphTransition = (shown: boolean): string =>
  `opacity ${FADE_MS}ms ${shown ? EASE_ENTER : EASE_EXIT}, transform ${SETTLE_MS}ms ${EASE_ENTER}`;

/**
 * A tab-bar glyph.
 *
 * Selected is the solid variant, unselected the same silhouette hollowed to a
 * band — the way iOS does it, and the reason the bar needs no coloured rule
 * underneath to say which tab you are on. The shape carries the state, so it
 * survives being seen by someone who cannot tell the two colours apart.
 *
 * Both variants are always in the DOM, stacked on one grid, and selection
 * crossfades between them: the hollow band dissolves as the solid rises into it
 * and settles up from 86%. Swapping the paths instead — which is what keying a
 * `<path>` on its own `d` did — is a hard cut, and a hard cut reads as a repaint
 * rather than as an answer to the tap. Only `opacity` and `transform` move, so
 * nothing here can reflow the bar, and both are off under reduced motion.
 */
export function TabIcon({
  name,
  active,
  size = 26,
}: {
  readonly name: TabIconName;
  readonly active: boolean;
  readonly size?: number;
}) {
  const still = useReducedMotion();
  const glyph = TAB_ICONS[name];

  // `view-box` pins the origin to the centre of the 24-unit grid rather than to
  // each variant's bounding box, so the settle stays concentric no matter how
  // the silhouettes are drawn.
  const layer = (shown: boolean) => ({
    transformBox: 'view-box' as const,
    transformOrigin: 'center' as const,
    opacity: shown ? 1 : 0,
    ...(still ? {} : { transition: glyphTransition(shown) }),
  });

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      fillRule="evenodd"
      className="tabbar__glyph"
      aria-hidden="true"
      focusable="false"
    >
      <g style={layer(!active)}>
        {glyph.light.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g
        style={{
          ...layer(active),
          // Always an explicit scale, never `none`: interpolating from a keyword
          // is the kind of thing one engine decides not to animate.
          transform: active || still ? 'scale(1)' : `scale(${SETTLE_FROM})`,
        }}
      >
        {glyph.fill.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  );
}

/**
 * The refresh mark: a 300° arc with a solid head.
 *
 * One glyph that is genuinely both, so it gets its own component rather than
 * forcing the renderer to mix modes for everything else. Decorative: it is only
 * ever used beside its button's label.
 */
export function RefreshIcon({ size = 16 }: { readonly size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
      <path
        d={STROKE_ICONS.refreshArc[0]}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth(size)}
        strokeLinecap="round"
      />
      <path d={FILL_ICONS.refreshHead[0]} fill="currentColor" />
    </svg>
  );
}
